import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { useShipmentFiltersContext } from "hooks";

import { ClientAppEvent } from "../../../../constants";
import { ShipmentPreview } from "../ShipmentPreview";

interface Props {
  shipments: CarrierCondensedShipment[];
  error: string | null;
  countError?: string | null;
  handlePageSelected: (e: React.ChangeEvent<unknown>, value: number) => void;
  pages: number;
  isLoading: boolean;
  page: number;
  partnerShipmentCount?: number;
  publicShipmentCount?: number;
}

export function ShipmentsList(props: Props) {
  const {
    shipments,
    error,
    countError,
    handlePageSelected,
    pages,
    isLoading,
    page,
    partnerShipmentCount,
    publicShipmentCount,
  } = props;

  const { filters, updateShipmentRelation } = useShipmentFiltersContext();
  const posthog = usePostHog();

  if (isLoading) {
    return (
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (error != null) {
    return (
      <Stack mb="1em" alignItems="center">
        <Typography color="red">{error}</Typography>
      </Stack>
    );
  }

  if (shipments.length === 0) {
    const isPartnerView = filters?.shipmentRelationship === "partner";

    return (
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="center"
        spacing={2}
      >
        <Typography sx={{ my: "2rem" }} textAlign="center" variant="body1">
          {isPartnerView
            ? "Your partners do not have any shipments posted at the moment."
            : "There are no available shipments at this time. Save your lane to be notified when something becomes available and try another search."}
        </Typography>
        {isPartnerView && (
          <Box>
            <Button
              variant="outlined"
              sx={{ mb: "1rem" }}
              onClick={() => {
                // Track analytics for recovery button
                posthog?.capture(
                  ClientAppEvent.CARRIER_PARTNER_RECOVERY_BUTTON_CLICKED,
                  {
                    partner_shipment_count: partnerShipmentCount ?? 0,
                    public_shipment_count: publicShipmentCount ?? 0,
                  },
                );
                updateShipmentRelation("public");
              }}
            >
              View Public Shipments
            </Button>
          </Box>
        )}
      </Stack>
    );
  }
  return (
    <Stack>
      {countError && (
        <Box mb="1rem">
          <Typography color="orange" variant="body2" textAlign="center">
            {countError}
          </Typography>
        </Box>
      )}
      {shipments.map((shipment) => (
        <Box key={shipment.info.id}>
          <Divider style={{ margin: "0" }} />
          <ShipmentPreview shipment={shipment} />
        </Box>
      ))}
      <Divider />
      <Box my="1rem" display="flex" alignItems="center" justifyContent="center">
        <Pagination count={pages} onChange={handlePageSelected} page={page} />
      </Box>
    </Stack>
  );
}
